import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Alert, Box, CircularProgress } from "@mui/material";

import Login from "../pages/Login";
import BelezaLogin from "../pages/BelezaLogin";
import BelezaAuth from "../pages/BelezaAuth";
import { isElloAppDomain, isElloPwaDomain, ELLO_PWA_PATH } from "../utils/elloDomains";
import RecuperarSenha from "../pages/RecuperarSenha";
import ValidateResetCode from "../pages/ValidateResetCode";
import ResetPassword from "../pages/ResetPassword";
import DynamicComponentLoader from "../components/DynamicComponentLoader";
import Dashboard from "../pages/Dashboard";
import ThemeSettings from "../pages/Themesettings";

import AlterarSenhaPrimeiroAcesso from "../pages/AlterarSenhaPrimeiroAcesso";
import EventoPublicPage from "../pages/EventoPublicPage";
import EventoKidsGerenciarPage from "../pages/Evento/EventoKidsGerenciarPage";
import BelezaPublicCheckout from "../pages/BelezaPublicCheckout/index";
import BelezaValePresentePublic from "../pages/BelezaValePresentePublic/index";
import BelezaCatalogoEspacos from "../pages/BelezaCatalogoEspacos/index";

import { useAuthContext } from "../contexts/AuthContext";
import { flattenScreens, permittedScreen, canonicalScreen } from "../utils/screenRegistry";
import { PublicLayoutProvider } from "../contexts/PublicLayoutContext";
import { sessionLoginPath, stationUnitFromPath } from "../utils/operatorStation";

const PWABeleza = React.lazy(() => import("../pages/PWABeleza"));
const LoginOperador = React.lazy(() => import("../pages/LoginOperador/index"));
const TerminalOperador = React.lazy(() => import("../pages/TerminalOperador/index"));
const PdvTerminal = React.lazy(() => import("../pages/PdvTerminal/index"));
const CommercialSupplierPortal = React.lazy(() => import("../pages/CommercialSupplierPortal"));
const CotacaoPublica = React.lazy(() => import("../pages/CotacaoPublica/index"));
const StationPage = ({ children }) => <React.Suspense fallback={<Box p={4} role="status" aria-label="Carregando estação"><CircularProgress /></Box>}>{children}</React.Suspense>;

// Estes componentes precisam ficar fora de AppRoutes. O AuthContext atualiza
// timeLeft a cada segundo; componentes declarados dentro de AppRoutes ganhavam
// uma nova identidade a cada render e o React desmontava/montava o PDV
// continuamente, causando o efeito de tela piscando/loop.
const PdvPage = ({ user, permissions }) => {
  const tipoUsuario = String(user?.tipoUsuario || "").toLowerCase();
  const allowed = permittedScreen(permissions || [], "PdvTerminal");

  return (
    <StationPage>
      {allowed ? (
        <PdvTerminal />
      ) : (
        <Alert severity="warning">Seu perfil não possui acesso ao Terminal PDV.</Alert>
      )}
    </StationPage>
  );
};

const FullscreenPage = ({ user, permissions }) => {
  const { componentName } = useParams();
  const tipoUsuario = String(user?.tipoUsuario || "").toLowerCase();
  const allowed = permittedScreen(permissions || [], componentName);

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "auto",
        bgcolor: "background.default",
      }}
    >
      {allowed ? (
        <DynamicComponentLoader component={componentName} />
      ) : (
        <Alert severity="warning">Seu perfil não possui acesso a esta tela.</Alert>
      )}
    </Box>
  );
};

const CatalogPage = () => {
  const { componentName } = useParams();
  return <Dashboard initialScreen={canonicalScreen(componentName)} />;
};

const normalizePath = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const getTelaRoute = (tela) => {
  const customRoute =
    tela?.rotaFrontend ||
    tela?.rota ||
    tela?.path ||
    tela?.caminho ||
    tela?.url ||
    tela?.route;

  if (customRoute) {
    return normalizePath(customRoute);
  }

  const telaNome = tela?.telaNome || tela?.nome || tela?.component;
  if (!telaNome) return "";

  return `/dashboard/${String(telaNome).toLowerCase()}`;
};

const getTelaComponent = (tela) =>
  tela?.telaNome || tela?.component || tela?.nome || "";

function ExternalElloPwa() {
  useEffect(() => { window.location.replace('https://pro.ellobeleza.com/'); }, []);
  return <Box role="status" p={3}>A abrir o aplicativo ÉLLO Professional…</Box>;
}

const AppRoutes = () => {
  // Rotas derivadas SINCRONAMENTE das permissões. Nenhuma janela entre o commit
  // do AuthContext e o useEffect que poderia desmontar o Dashboard activo.

  const location = useLocation();
  const onPwaHost = isElloPwaDomain();
  const onAppHost = isElloAppDomain();
  const navigate = useNavigate();
  const { isAuthenticated, user, permissions, loading, refreshPermissions } = useAuthContext();
  const lastLoggedPath = useRef("");

  const publicRoutes = useMemo(
    () => [
      "/Fe/pedido-oracao",
      "/Fe/testemunho",
      "/Fe/batismo-2025",
      "/login",
      "/pwa/colaborador",
      "/pwa/beleza",
      "/loginfilter",
      "/producao/login",
      "/beleza/contratar",
      "/beleza/contratar/sucesso",
      "/beleza/vale-presente",
      "/beleza/vale-presente/sucesso",
      "/beleza/espacos",
      "/cotacao",
      "/compras/fornecedor",
      "/Security/recuperar-senha",
      "/Security/validate-reset-code",
      "/Security/reset-password",
      "/Security/alterar-senha-primeiro-acesso",
    ],
    []
  );

  const checkIsPublicPath = (pathname) =>
    publicRoutes.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    );

  const isCheckinPublic = location.pathname.startsWith("/eventos/checkin");

  const isBelezaCheckout = location.pathname.startsWith("/beleza/contratar");

  const isBelezaValePresente = location.pathname.startsWith(
    "/beleza/vale-presente"
  );

  const isEventoGerenciarPublic =
    location.pathname.startsWith("/dashboard/evento/") &&
    location.pathname.includes("/gerenciar");

  const isPublicPath =
    checkIsPublicPath(location.pathname) ||
    isCheckinPublic ||
    isEventoGerenciarPublic ||
    isBelezaCheckout ||
    isBelezaValePresente;

  useEffect(() => {
    if (location.pathname !== lastLoggedPath.current) {
      lastLoggedPath.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && isAuthenticated && user?.unidadeId &&
        location.pathname.startsWith("/dashboard")) {
      refreshPermissions();
    }
  }, [location.pathname, loading, isAuthenticated, user?.unidadeId, refreshPermissions]);

  const tipoUsuario = (user?.tipoUsuario || "").toString().toLowerCase();
  const isPrivileged =
    tipoUsuario === "root" || tipoUsuario === "administrador";

  useEffect(() => {
    if (loading || !isAuthenticated || !user || location.pathname === "/compras/fornecedor") return;

    const rotaInicial = "/loginfilter";

    if (!user.email?.trim()) {
      navigate("/login", { replace: true });
      return;
    }

    if (
      user.primeiroAcesso &&
      location.pathname !== "/Security/alterar-senha-primeiro-acesso"
    ) {
      navigate("/Security/alterar-senha-primeiro-acesso", {
        replace: true,
        state: { email: user.email, senhaAtual: "" },
      });
      return;
    }

    if (!onPwaHost && isPublicPath && ["/", "/login"].includes(location.pathname)) {
      navigate(rotaInicial, { replace: true });
    }
  }, [
    loading,
    isAuthenticated,
    user,
    location.pathname,
    navigate,
    permissions,
    isPublicPath,
    onPwaHost,
  ]);

  const userScreens = useMemo(() => {
    if (!permissions?.length) return [];
    const screens = flattenScreens(permissions).map(tela => {
      const path = getTelaRoute(tela), component = getTelaComponent(tela);
      return path && component ? { path, component } : null;
    }).filter(Boolean);
    return Array.from(new Map(screens.map(screen => [
      `${screen.path.toLowerCase()}::${screen.component}`, screen
    ])).values());
  }, [permissions]);

  // Separar fachadas por HOST, não por querystring. Permissões/licenças continuam na API.
  const dedicatedSecurity = (route) => (onAppHost || onPwaHost)
    ? <BelezaAuth /> : route;

  // Publicação antiga: nenhum conteúdo do PWA deve ser servido no domínio base.
  // É um redirecionamento somente da rota legada, nunca do login base do I9.
  const legacyPwaLink = !onPwaHost && !onAppHost && location.pathname.startsWith('/pwa/');

  const routeElements = (
    <Routes>
      {/* Públicas — Fé / Institucional */}
      <Route
        path="/Fe/pedido-oracao"
        element={<DynamicComponentLoader component="PedidoOracao" />}
      />
      <Route
        path="/Fe/testemunho"
        element={<DynamicComponentLoader component="Testemunho" />}
      />
      <Route
        path="/Fe/batismo-2025"
        element={<DynamicComponentLoader component="InscricaoEvento" />}
      />

      {/* Públicas — Segurança */}
      <Route path="/login" element={onAppHost ? <BelezaLogin /> : <Login />} />
      <Route path="/" element={<Navigate to={onPwaHost ? ELLO_PWA_PATH : (isAuthenticated ? "/loginfilter" : "/login")} replace />} />
      <Route path="/pwa/colaborador" element={<StationPage><PWABeleza /></StationPage>} />
      <Route path="/pwa/beleza" element={<StationPage><PWABeleza /></StationPage>} />
      <Route path="/producao/login/:unidadeId" element={<StationPage><LoginOperador /></StationPage>} />
      <Route path="/producao/terminal/:unidadeId" element={<StationPage><TerminalOperador /></StationPage>} />
      <Route path="/Security/recuperar-senha" element={dedicatedSecurity(<RecuperarSenha />)} />
      <Route
        path="/Security/validate-reset-code"
        element={dedicatedSecurity(<ValidateResetCode />)}
      />
      <Route path="/Security/reset-password" element={dedicatedSecurity(<ResetPassword />)} />
      <Route
        path="/Security/alterar-senha-primeiro-acesso"
        element={dedicatedSecurity(<AlterarSenhaPrimeiroAcesso />)}
      />

      {/* Pública — Portal do fornecedor */}
      <Route path="/compras/fornecedor" element={<StationPage><CommercialSupplierPortal /></StationPage>} />
      <Route path="/cotacao/:token" element={<StationPage><CotacaoPublica /></StationPage>} />

      {/* Públicas — Eventos / Etiqueta */}
      <Route path="/eventos/checkin" element={<EventoPublicPage />} />
      <Route path="/producao/etiqueta-coplac" element={<Navigate to="/dashboard/etiqueta" replace />} />
      <Route
        path="/dashboard/evento/:id/gerenciar"
        element={<EventoKidsGerenciarPage />}
      />

      {/* Públicas — SaaS Beleza */}
      <Route path="/beleza/contratar" element={<BelezaPublicCheckout />} />
      <Route
        path="/beleza/contratar/sucesso"
        element={<BelezaPublicCheckout />}
      />
      <Route
        path="/beleza/vale-presente"
        element={<BelezaValePresentePublic />}
      />
      <Route
        path="/beleza/vale-presente/sucesso"
        element={<BelezaValePresentePublic />}
      />

      <Route path="/beleza/espacos/:token" element={<BelezaCatalogoEspacos />} />

      {/* Técnicas */}
      <Route path="/fullscreen/:componentName" element={<FullscreenPage user={user} permissions={permissions} />} />
      <Route path="/pdv" element={<PdvPage user={user} permissions={permissions} />} />

      {/* Privadas fixas do shell */}
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/dashboard/theme-settings" element={<ThemeSettings />} />
      <Route path="/admin/hub-licensing" element={tipoUsuario === "root" ? <Navigate to="/dashboard/hub-licensing" replace /> : <Alert severity="error">Acesso exclusivo ao administrador root.</Alert>} />
      <Route path="/dashboard/hub-licensing" element={tipoUsuario === "root" ? <Dashboard initialScreen="HubLicenseMappings" /> : <Alert severity="error">Acesso exclusivo ao administrador root.</Alert>} />
      <Route
        path="/loginfilter"
        element={<DynamicComponentLoader component="LoginFilter" />}
      />
      <Route
        path="/dashboard/inscritos"
        element={<DynamicComponentLoader component="Inscritos" />}
      />

      {/* Nenhuma tela operacional é autorizada por rotas fixas.
          As rotas abaixo são derivadas das telas efetivas da unidade/versão. */}

      {/* Privadas dinâmicas vindas do controle de versão/módulo/tela/permissão */}
      {userScreens.map((screen) => (
        <Route
          key={`${screen.path}-${screen.component}`}
          path={screen.path}
          element={<Dashboard initialScreen={screen.component} />}
        />
      ))}

      <Route path="/dashboard/:componentName" element={<CatalogPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );

  // Somente a restauração inicial bloqueia as rotas. Atualizações posteriores
  // de permissões (focus, visibilitychange, navegação) NÃO podem desmontar o
  // Dashboard nem o formulário corrente. Revogações continuam a atualizar o
  // contexto e a autorização de cada operação permanece no backend.
  if (loading) {
    return <Box role="status" aria-label="Restaurando sessão" p={4} textAlign="center"><CircularProgress /></Box>;
  }

  if (legacyPwaLink) {
    return <ExternalElloPwa />;
  }

  // Em app.ellobeleza.com, só entrar na gestão Beleza. Rotas institucionais,
  // eventos, compras, operador e PWA não existem nesta fachada.
  if (onAppHost && !(/^(\/|\/login\/?|\/loginfilter\/?|\/dashboard(?:\/.*)?|\/fullscreen\/.*|\/pdv\/?|\/Security\/(?:recuperar-senha|validate-reset-code|reset-password|alterar-senha-primeiro-acesso)\/?)$/i).test(location.pathname)) {
    return <Navigate to={isAuthenticated ? '/loginfilter' : '/login'} replace />;
  }

  // O endereço público do PWA nunca abre o Gestor nem seu login genérico.
  // Exceção controlada: telas de recuperação/primeiro acesso precisam funcionar.
  if (onPwaHost && ![
    ELLO_PWA_PATH, "/pwa/beleza", "/Security/alterar-senha-primeiro-acesso",
    "/Security/recuperar-senha", "/Security/validate-reset-code",
    "/Security/reset-password",
  ].includes(location.pathname)) {
    return <Navigate to={ELLO_PWA_PATH} replace />;
  }
  // PWA e painel administrativo têm fachadas distintas, mesmo com o mesmo build.
  if (onAppHost && location.pathname.startsWith("/pwa/")) {
    return <Navigate to={isAuthenticated ? "/loginfilter" : "/login"} replace />;
  }

  if (!isAuthenticated && !isPublicPath) {
    return <Navigate to={sessionLoginPath(location.pathname)} replace />;
  }

  if (
    isAuthenticated &&
    !isPrivileged &&
    !permissions?.length &&
    !isPublicPath &&
    !stationUnitFromPath(location.pathname)
  ) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        px={2}
        textAlign="center"
      >
        <p>
          ⚠️ Não foi possível carregar suas permissões. Tente novamente ou
          contate o suporte.
        </p>
      </Box>
    );
  }

  return isPublicPath ? (
    <PublicLayoutProvider>{routeElements}</PublicLayoutProvider>
  ) : (
    routeElements
  );
};

export default AppRoutes;
